import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import {
  CheckoutExtensionUI,
  CheckoutUpsellExtension,
  UpsellFreeShippingThresholdReplaceValues,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  IUpsellThresholdTypes,
  IUpsellprioritizeRecommendedItemsBy,
} from '@pdqdev/pdq-shared/dist/interfaces/IThresholdTypes';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  RichTextAreaComponent,
  RichTextCompTextFormat,
} from '@app/storybook/components/rich-textarea/rich-textarea.component';
import { capitalize } from '@utils/string-utils';

interface ThresholdTypeOption {
  text: string;
  value: IUpsellThresholdTypes;
  defaultChecked: boolean;
  checked: (val: string) => boolean;
}

interface DisplayTypeOption {
  text: string;
  value: CheckoutUpsellExtension.DisplayType;
  tooltip?: string;
  tooltipIcon?: string;
  defaultChecked?: boolean;
}

interface PrioritizeOption {
  text: string;
  value: IUpsellprioritizeRecommendedItemsBy;
  subText: string;
}

@Component({
  selector: 'checkout-upsell-appearance',
  templateUrl: './checkout-upsell-appearance.component.html',
  styleUrls: ['./checkout-upsell-appearance.component.scss'],
  standalone: false,
})
export class CheckoutUpsellAppearanceComponent {
  @Input() formGroup: UntypedFormGroup;

  readonly dragHandleRectangles = Array(6).fill(0);
  @Input() isPreviewThresholdMode: boolean = true;
  @Output() isPreviewThresholdModeChange = new EventEmitter<boolean>();
  @Output() onFormValueChange = new EventEmitter<any>();

  @ViewChild('textWithThreshold') textWithThresholdRef!: RichTextAreaComponent;
  @ViewChild('textWithoutThreshold') textWithoutThresholdRef!: RichTextAreaComponent;

  UpsellThresholdTypes = IUpsellThresholdTypes;
  UpsellFreeShippingThresholdReplaceValues = UpsellFreeShippingThresholdReplaceValues;
  UpsellPrioritizeRecommendedItemsBy = IUpsellprioritizeRecommendedItemsBy;
  disabledFormats: RichTextCompTextFormat[] = ['underline', 'align', 'list', 'header'];

  thresholdTypeOptions: ThresholdTypeOption[] = [
    {
      text: 'Cart Value',
      value: IUpsellThresholdTypes.CartValue,
      defaultChecked: true,
      checked: (val: string) => val === IUpsellThresholdTypes.CartValue,
    },
    {
      text: 'Item Count',
      value: IUpsellThresholdTypes.ItemCount,
      defaultChecked: false,
      checked: (val: string) => val === IUpsellThresholdTypes.ItemCount,
    },
    {
      text: 'No Threshold',
      value: IUpsellThresholdTypes.NoThreshold,
      defaultChecked: false,
      checked: (val: string) => val === IUpsellThresholdTypes.NoThreshold,
    },
  ];

  displayTypeOptions: DisplayTypeOption[] = [
    {
      text: 'Product Cards',
      value: CheckoutUpsellExtension.DisplayType.CARDS,
      tooltip: 'Currently desktop only. On mobile devices, the rows format is used to ensure compatibility.',
      tooltipIcon: 'attention',
      defaultChecked: true,
    },
    {
      text: 'Product Row',
      value: CheckoutUpsellExtension.DisplayType.ROW,
    },
  ];

  prioritizeRecommendedItemsByOptions: PrioritizeOption[] = [
    {
      text: 'Profitability',
      value: IUpsellprioritizeRecommendedItemsBy.Profitability,
      subText: 'Show items with the highest profit margins first.',
    },
    {
      text: 'High Price',
      value: IUpsellprioritizeRecommendedItemsBy.HighPrice,
      subText: 'Show most expensive items first.',
    },
    {
      text: 'Low Price',
      value: IUpsellprioritizeRecommendedItemsBy.LowPrice,
      subText: 'Show the cheapest items that help unlock free shipping first.',
    },
    {
      text: 'Selling Plan',
      value: IUpsellprioritizeRecommendedItemsBy.SellingPlan,
      subText: 'Show items only from a specific selling plan.',
    },
  ];

  Appearances = ['default', ...Object.values(CheckoutExtensionUI.Appearance)];
  appearanceToDisplayText = (appearance: CheckoutExtensionUI.Appearance) => capitalize(appearance);

  get additionalDisplayTextsFormArray(): UntypedFormArray {
    return this.formGroup.get('settings.additionalDisplayTexts') as UntypedFormArray;
  }

  togglePreviewMode() {
    this.isPreviewThresholdMode = !this.isPreviewThresholdMode;
    this.isPreviewThresholdModeChange.emit(this.isPreviewThresholdMode);
    this.onFormValueChange.emit(this.formGroup.value);
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.additionalDisplayTextsFormArray.controls, event.previousIndex, event.currentIndex);
    this.additionalDisplayTextsFormArray.controls.forEach((control, index) => {
      control.get('priority').setValue(index);
    });
    this.formGroup.markAsDirty();
  }
}
